<script setup lang="ts">
// ================================
// Le bandeau de décision et son fil (DEV-2284, lot A ; D3).
//
// Collant en haut de la page, jamais replié. Des valeurs servies seulement :
// jamais une phrase, une réserve ni une couleur d'alerte ; un montant non
// servi s'écrit « Montant non servi », jamais 0.
// Le fil : cinq ancres (Cadre, 1 Comprendre, 2 Choisir, 3 Partager, 4 Agir et
// suivre) ; une ancre qui conduit à une section repliée la déroule ; ce qui
// est sans objet ou absent est estompé. À droite, « Tout déplier », qui
// devient « Tout replier » quand tout est ouvert (maquette v0.4).
// ================================
import type { ValeurDuBandeau } from "@modules/courtage/utils/optim-turpe-v2.entetes"

export type EtapeDuFil = {
    cible: string
    numero: number | null
    libelle: string
    /** Sans objet ou absente dans cet état : l'ancre est estompée. */
    estompee: boolean
}

type Props = {
    valeurs: ValeurDuBandeau[]
    etapes: EtapeDuFil[]
    toutOuvert: boolean
}
defineProps<Props>()

const emit = defineEmits<{
    aller: [cible: string]
    tout: [ouvrir: boolean]
}>()
</script>

<template>
    <div
        class="optim-turpe-v2-bandeau sticky top-0 z-10 flex flex-col gap-2 rounded-lg border border-surface-200 bg-white/95 px-5 py-3 backdrop-blur"
        data-testid="optim-turpe-v2-bandeau"
    >
        <dl class="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <div
                v-for="v in valeurs"
                :key="v.terme"
                class="flex items-baseline gap-1.5"
                :class="v.secondaire ? 'hidden sm:flex' : ''"
            >
                <dt class="text-xs text-primary-500">{{ v.terme }}</dt>
                <dd
                    class="text-sm font-semibold text-primary-900"
                    data-testid="optim-turpe-v2-bandeau-valeur"
                >
                    {{ v.valeur }}
                </dd>
            </div>
        </dl>
        <nav aria-label="Les sections de la page">
            <ol class="flex flex-wrap items-center gap-x-1 gap-y-1">
                <li v-for="e in etapes" :key="e.cible">
                    <button
                        type="button"
                        class="rounded px-2 py-1 text-xs hover:bg-surface-100"
                        :class="
                            e.estompee ? 'text-primary-300' : 'text-primary-700'
                        "
                        :data-testid="`optim-turpe-v2-fil-${e.cible}`"
                        @click="emit('aller', e.cible)"
                    >
                        <span v-if="e.numero" class="mr-1 font-semibold">{{
                            e.numero
                        }}</span
                        >{{ e.libelle }}
                    </button>
                </li>
                <li class="ml-auto">
                    <button
                        type="button"
                        class="rounded px-2 py-1 text-xs font-medium text-primary-800 underline-offset-2 hover:underline"
                        data-testid="optim-turpe-v2-tout"
                        @click="emit('tout', !toutOuvert)"
                    >
                        {{ toutOuvert ? "Tout replier" : "Tout déplier" }}
                    </button>
                </li>
            </ol>
        </nav>
    </div>
</template>
